import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, BadgeCheck, Camera, Filter, Pin } from "lucide-react";
import { toast } from "sonner";
import StoreLayout from "../components/StoreLayout";
import PostFitModal from "../components/PostFitModal";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function absoluteImg(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

const SORTS = [
  { id: "latest", label: "Latest" },
  { id: "top", label: "Most Liked" },
];

function PostCard({ post, onToggleLike }) {
  const liked = !!post.liked_by_viewer;
  return (
    <article
      className="break-inside-avoid mb-6 bg-[#0A0A0A] border border-[#27272A] hover:border-[#CFF048]/40 transition-colors group"
      data-testid={`community-post-${post.id}`}
    >
      <Link to={`/community/post/${post.id}`} className="block relative">
        <img
          src={absoluteImg(post.image_url)}
          alt={post.caption || "Fit post"}
          className="w-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
        />
        {post.pinned && (
          <span className="absolute top-3 left-3 bg-[#CFF048] text-black px-2 py-1 label-mono text-[10px] flex items-center gap-1">
            <Pin size={11} /> FEATURED
          </span>
        )}
      </Link>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 bg-[#CFF048] text-black font-display font-black text-xs flex items-center justify-center">
            {(post.user_name || "?")[0].toUpperCase()}
          </div>
          <span className="font-display font-bold text-sm truncate">{post.user_name || "Anonymous"}</span>
          <BadgeCheck size={14} className="text-[#CFF048]" title="Verified Cult Lyf buyer" />
        </div>
        {post.caption && (
          <p className="text-[#A1A1AA] text-sm leading-relaxed line-clamp-3">{post.caption}</p>
        )}
        {post.product_slug && (
          <Link
            to={`/product/${post.product_slug}`}
            className="inline-block label-mono text-[#CFF048] mt-3 hover:underline truncate max-w-full"
            data-testid={`post-product-link-${post.id}`}
          >
            // {post.product_name}
          </Link>
        )}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#27272A]">
          <button
            onClick={(e) => { e.preventDefault(); onToggleLike(post); }}
            className={`flex items-center gap-1.5 label-mono transition-colors ${
              liked ? "text-[#CFF048]" : "text-[#71717A] hover:text-white"
            }`}
            data-testid={`like-btn-${post.id}`}
          >
            <Heart size={16} className={liked ? "fill-[#CFF048]" : ""} />
            {post.like_count || 0}
          </button>
          <Link
            to={`/community/post/${post.id}`}
            className="flex items-center gap-1.5 label-mono text-[#71717A] hover:text-white"
            data-testid={`comment-btn-${post.id}`}
          >
            <MessageCircle size={16} />
            {post.comment_count || 0}
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Community() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("latest");
  const [showModal, setShowModal] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get(`/community/posts?sort=${sort}`)
      .then((r) => setPosts(r.data || []))
      .catch(() => toast.error("Failed to load posts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [sort]);

  useEffect(() => {
    if (!user) { setEligible(false); setEligibilityChecked(true); return; }
    api
      .get("/community/me/eligibility")
      .then((r) => setEligible(!!r.data?.verified_buyer))
      .catch(() => setEligible(false))
      .finally(() => setEligibilityChecked(true));
  }, [user]);

  const onToggleLike = async (post) => {
    if (!user) {
      toast.error("Sign in to react");
      navigate("/login");
      return;
    }
    // optimistic
    setPosts((cur) =>
      cur.map((p) =>
        p.id === post.id
          ? {
              ...p,
              liked_by_viewer: !p.liked_by_viewer,
              like_count: (p.like_count || 0) + (p.liked_by_viewer ? -1 : 1),
            }
          : p
      )
    );
    try {
      await api.post(`/community/posts/${post.id}/like`);
    } catch {
      toast.error("Couldn't react. Try again.");
      load();
    }
  };

  const onPostClick = () => {
    if (!user) {
      toast.error("Sign in first");
      navigate("/login?redirect=/community");
      return;
    }
    if (!eligible) {
      toast.error("Only verified Cult Lyf buyers can post", {
        description: "Place an order to unlock posting on the wall.",
      });
      return;
    }
    setShowModal(true);
  };

  return (
    <StoreLayout>
      {/* Hero */}
      <section className="border-b border-[#27272A] bg-gradient-to-b from-[#0A0A0A] to-black">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16">
          <p className="label-mono text-[#CFF048]">// THE CULT WALL</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mt-3 leading-[0.95]">
            Stay Different.<br />
            <span className="text-[#CFF048]">Stay Seen.</span>
          </h1>
          <p className="text-[#A1A1AA] mt-5 max-w-2xl">
            Real fits from real Cult Lyf buyers. Drop your photo, tag your tee, hype the squad.
            Only verified buyers can post — everyone watches.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="community-controls">
            <button
              onClick={onPostClick}
              className="btn-neon flex items-center gap-2"
              data-testid="post-fit-cta"
            >
              <Camera size={16} /> POST YOUR FIT
            </button>
            {user && eligibilityChecked && !eligible && (
              <Link
                to="/shop"
                className="label-mono text-[#71717A] hover:text-[#CFF048] underline underline-offset-4"
                data-testid="not-verified-cta"
              >
                // BUY A TEE TO UNLOCK POSTING →
              </Link>
            )}
            {!user && (
              <Link
                to="/login?redirect=/community"
                className="label-mono text-[#71717A] hover:text-[#CFF048] underline underline-offset-4"
              >
                // SIGN IN →
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-8">
        <div className="flex items-center gap-2 flex-wrap" data-testid="community-sort">
          <Filter size={14} className="text-[#71717A]" />
          <span className="label-mono text-[#71717A] mr-2">SORT</span>
          {SORTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              className={`label-mono px-3 py-1.5 border transition-colors ${
                sort === s.id
                  ? "border-[#CFF048] text-[#CFF048] bg-[#CFF048]/10"
                  : "border-[#27272A] text-[#71717A] hover:border-white hover:text-white"
              }`}
              data-testid={`sort-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </section>

      {/* Wall */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        {loading ? (
          <div className="text-center label-mono text-[#71717A] py-20">// LOADING THE WALL...</div>
        ) : posts.length === 0 ? (
          <div className="border border-[#27272A] p-16 text-center" data-testid="community-empty">
            <p className="label-mono text-[#CFF048]">// THE WALL IS EMPTY</p>
            <h3 className="font-display font-black text-2xl mt-3">Be the first to drop a fit.</h3>
            <p className="text-[#71717A] mt-2 max-w-md mx-auto">
              The first 10 approved posts get pinned to the top. Limited window.
            </p>
            <button onClick={onPostClick} className="btn-neon mt-6">POST YOUR FIT</button>
          </div>
        ) : (
          <div
            className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6"
            data-testid="community-wall"
          >
            {posts.map((p) => (
              <PostCard key={p.id} post={p} onToggleLike={onToggleLike} />
            ))}
          </div>
        )}
      </section>

      <PostFitModal open={showModal} onClose={() => setShowModal(false)} onPosted={load} />
    </StoreLayout>
  );
}
